import { useCallback, useRef, useEffect } from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Link from '@tiptap/extension-link';
import Image from '@tiptap/extension-image';
import Placeholder from '@tiptap/extension-placeholder';
import TextAlign from '@tiptap/extension-text-align';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Bold, Italic, Strikethrough, Heading2, Heading3, List, ListOrdered,
  Quote, Code, Link2, ImagePlus, AlignLeft, AlignCenter, AlignRight,
  Undo, Redo, Pilcrow,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ToolbarButton = ({ onClick, active, disabled, title, children, testid }) => (
  <button
    type="button"
    onClick={onClick}
    disabled={disabled}
    title={title}
    data-testid={testid}
    className={`p-2 text-sm transition-colors ${
      active ? 'bg-zinc-900 text-white' : 'text-zinc-700 hover:bg-zinc-100'
    } disabled:opacity-40 disabled:cursor-not-allowed`}
  >
    {children}
  </button>
);

const Divider = () => <span className="w-px self-stretch bg-zinc-200 mx-0.5" />;

export const RichTextEditor = ({ value, onChange, placeholder = 'Write your article…' }) => {
  const fileInputRef = useRef(null);

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: { levels: [2, 3] },
      }),
      Link.configure({
        openOnClick: false,
        autolink: true,
        HTMLAttributes: { class: 'text-red-600 underline underline-offset-2' },
      }),
      Image.configure({
        HTMLAttributes: { class: 'rounded my-3 max-w-full h-auto' },
      }),
      Placeholder.configure({ placeholder }),
      TextAlign.configure({ types: ['heading', 'paragraph'] }),
    ],
    content: value || '',
    onUpdate: ({ editor }) => onChange?.(editor.getHTML()),
    editorProps: {
      attributes: {
        class: 'prose prose-zinc max-w-none focus:outline-none min-h-[300px] px-4 py-4',
        'data-testid': 'rich-text-content',
      },
    },
  });

  // Editor is UNCONTROLLED for content — parent only seeds the initial value at mount.
  // To swap content (e.g. when editing a different article), the parent should re-mount
  // this component via a `key` prop (e.g. <RichTextEditor key={article?.id || 'new'} … />).
  // This avoids a controlled-sync race that previously stripped just-inserted images.
  // (We DO still listen for an explicit "blank reset" so 'New Article' clears the editor.)
  useEffect(() => {
    if (!editor) return;
    if (value === '' && editor.getHTML() !== '<p></p>' && !editor.isFocused) {
      editor.commands.clearContent(false);
    }
  }, [value, editor]);

  const insertImageByUrl = useCallback(() => {
    if (!editor) return;
    const url = window.prompt('Paste image URL:');
    if (url && url.trim()) {
      editor.chain().focus().setImage({ src: url.trim() }).run();
    }
  }, [editor]);

  const uploadImage = useCallback(async (file) => {
    if (!file || !editor) return;
    if (!file.type.startsWith('image/')) {
      toast.error('Only image files are supported');
      return;
    }
    const fd = new FormData();
    fd.append('file', file);
    const t = toast.loading('Uploading image…');
    try {
      const res = await axios.post(`${API}/uploads/blog-image`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      const url = res.data?.url;
      if (url) {
        editor.chain().focus().setImage({ src: url }).run();
        toast.success('Image inserted', { id: t });
      } else {
        toast.error('Upload failed', { id: t });
      }
    } catch (err) {
      console.error('[blog-editor] upload failed:', err);
      const msg = err?.response?.data?.detail || 'Upload failed';
      toast.error(msg, { id: t });
    }
  }, [editor]);

  const setLink = useCallback(() => {
    if (!editor) return;
    const prev = editor.getAttributes('link').href;
    const url = window.prompt('URL:', prev || 'https://');
    if (url === null) return;
    if (url === '') {
      editor.chain().focus().extendMarkRange('link').unsetLink().run();
      return;
    }
    editor.chain().focus().extendMarkRange('link').setLink({ href: url }).run();
  }, [editor]);

  if (!editor) return null;

  return (
    <div className="border border-zinc-300 bg-white" data-testid="rich-text-editor">
      {/* Toolbar */}
      <div className="border-b border-zinc-200 bg-zinc-50 px-2 py-1 flex items-center gap-0.5 flex-wrap sticky top-0 z-10">
        <ToolbarButton onClick={() => editor.chain().focus().toggleBold().run()} active={editor.isActive('bold')} title="Bold (⌘B)" testid="rte-bold"><Bold className="w-4 h-4" /></ToolbarButton>
        <ToolbarButton onClick={() => editor.chain().focus().toggleItalic().run()} active={editor.isActive('italic')} title="Italic (⌘I)" testid="rte-italic"><Italic className="w-4 h-4" /></ToolbarButton>
        <ToolbarButton onClick={() => editor.chain().focus().toggleStrike().run()} active={editor.isActive('strike')} title="Strikethrough" testid="rte-strike"><Strikethrough className="w-4 h-4" /></ToolbarButton>
        <Divider />
        <ToolbarButton onClick={() => editor.chain().focus().setParagraph().run()} active={editor.isActive('paragraph')} title="Paragraph" testid="rte-paragraph"><Pilcrow className="w-4 h-4" /></ToolbarButton>
        <ToolbarButton onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()} active={editor.isActive('heading', { level: 2 })} title="Heading 2" testid="rte-h2"><Heading2 className="w-4 h-4" /></ToolbarButton>
        <ToolbarButton onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()} active={editor.isActive('heading', { level: 3 })} title="Heading 3" testid="rte-h3"><Heading3 className="w-4 h-4" /></ToolbarButton>
        <Divider />
        <ToolbarButton onClick={() => editor.chain().focus().toggleBulletList().run()} active={editor.isActive('bulletList')} title="Bullet list" testid="rte-ul"><List className="w-4 h-4" /></ToolbarButton>
        <ToolbarButton onClick={() => editor.chain().focus().toggleOrderedList().run()} active={editor.isActive('orderedList')} title="Numbered list" testid="rte-ol"><ListOrdered className="w-4 h-4" /></ToolbarButton>
        <ToolbarButton onClick={() => editor.chain().focus().toggleBlockquote().run()} active={editor.isActive('blockquote')} title="Blockquote" testid="rte-quote"><Quote className="w-4 h-4" /></ToolbarButton>
        <ToolbarButton onClick={() => editor.chain().focus().toggleCodeBlock().run()} active={editor.isActive('codeBlock')} title="Code block" testid="rte-code"><Code className="w-4 h-4" /></ToolbarButton>
        <Divider />
        <ToolbarButton onClick={() => editor.chain().focus().setTextAlign('left').run()} active={editor.isActive({ textAlign: 'left' })} title="Align left" testid="rte-align-left"><AlignLeft className="w-4 h-4" /></ToolbarButton>
        <ToolbarButton onClick={() => editor.chain().focus().setTextAlign('center').run()} active={editor.isActive({ textAlign: 'center' })} title="Align center" testid="rte-align-center"><AlignCenter className="w-4 h-4" /></ToolbarButton>
        <ToolbarButton onClick={() => editor.chain().focus().setTextAlign('right').run()} active={editor.isActive({ textAlign: 'right' })} title="Align right" testid="rte-align-right"><AlignRight className="w-4 h-4" /></ToolbarButton>
        <Divider />
        <ToolbarButton onClick={setLink} active={editor.isActive('link')} title="Insert link" testid="rte-link"><Link2 className="w-4 h-4" /></ToolbarButton>
        <div className="relative group">
          <ToolbarButton onClick={() => fileInputRef.current?.click()} title="Insert image" testid="rte-image"><ImagePlus className="w-4 h-4" /></ToolbarButton>
          {/* Tiny chevron offering URL alternative */}
          <button
            type="button"
            onClick={insertImageByUrl}
            className="absolute -bottom-1 -right-1 text-[8px] bg-zinc-700 text-white px-1 hover:bg-red-600"
            title="Insert by URL"
            data-testid="rte-image-by-url"
          >URL</button>
        </div>
        <input
          ref={fileInputRef}
          type="file"
          accept="image/png,image/jpeg,image/gif,image/webp,image/svg+xml"
          className="hidden"
          onChange={(e) => {
            const f = e.target.files?.[0];
            if (f) uploadImage(f);
            e.target.value = '';  // allow re-uploading the same file
          }}
          data-testid="rte-file-input"
        />
        <Divider />
        <div className="ml-auto flex items-center gap-0.5">
          <ToolbarButton onClick={() => editor.chain().focus().undo().run()} disabled={!editor.can().undo()} title="Undo (⌘Z)" testid="rte-undo"><Undo className="w-4 h-4" /></ToolbarButton>
          <ToolbarButton onClick={() => editor.chain().focus().redo().run()} disabled={!editor.can().redo()} title="Redo (⌘⇧Z)" testid="rte-redo"><Redo className="w-4 h-4" /></ToolbarButton>
        </div>
      </div>

      {/* Content */}
      <EditorContent editor={editor} />
    </div>
  );
};

export default RichTextEditor;
